import {t} from '@lingui/macro';
import _, {compact, flatten} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {LocalizeRenderer} from '../../../../srcCommon/internationalization/internationalizationContext';
import {
  buildCustomViewCounterKey,
  buildPrivateInboxCounterKey,
  discussionsCounterKey,
  findBoldCounter,
  selfAssignedCounterKey,
  selfInboxCounterKey,
  sharedWithMeCounterKey,
} from '../../../helpers/counterHelpers';
import {
  buildTeamInboxPath,
  buildTeammateDisplayName,
  buildTeammateInboxPath,
  buildTeammatePath,
  buildTeamPath,
  findSelectedFilterForTeam,
  findSelectedFilterForTeammate,
} from '../../../helpers/modelHelpers';
import {buildCustomViewPath, computeFlatVisualCustomViews} from '../../../helpers/models/customViewHelpers';
import {isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {defaultIsCustomViewExpanded, dummyFindIsExpanded} from '../../../helpers/visualCustomViewHelpers';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {
  InboxesFiltersEnum,
  InboxesSearchLocationsEnum,
  RInboxesFilter,
  TeammatesFoldersEnum,
  TeammatesMessageFoldersEnum,
  TeamsFoldersEnum,
} from '../../../models/url/inboxesUrlModel';
import {getHasPermission, getHasSharedPermission} from '../../../store/data/access/accessSelectors';
import {getBoldCounters} from '../../../store/data/dataSelectors';
import {
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../../store/data/inboxes/inboxesSelectors';
import {updateSelectedSideFrameIdAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {
  getLastSelectedSideFrameId,
  getSelectedFilters,
  getSelectedSideFrameId,
} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {makeGetCountersVisibility} from '../../../store/data/preferences/countersSelectors';
import {
  getIsInboxVisible,
  getShouldAlphabetizeWorkspace,
  getShouldUseOpenFilterForTeams,
} from '../../../store/data/preferences/preferencesSelectors';
import {getStarredTagIds} from '../../../store/data/tags/tagsSelectors';
import {getFindOnlineStatus} from '../../../store/data/teammates/onlineTeammatesSelectors';
import {getUser, getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {getUserTeams} from '../../../store/data/teams/teamsSelectors';
import {
  makeGetUserVisualWorkspaceTeammateWorkspaceTags,
  makeGetVisualWorkspaceTeammateWorkspaceVisualCustomViews,
} from '../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {getWorkspaceTeammatesWithLazyValidationSorted} from '../../../store/data/workspace/workspaceSelectors';
import {
  getHasNewContactsManagerV0,
  getShouldHideAuditTrail,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersPath} from '../../../store/panels/inboxes/parameters/inboxesParametersPathHelpers';
import {getLocalize, Localizer} from '../../../store/stateless/internationalizationSelectors';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {getSidePanelFrames} from '../../../store/stateless/sidePanelFrameSelectors';
import {buildApps} from '../../app/topBar/topBarHelpers';
import {findFilters} from '../cells/header/cellsHeaderContainer';
import {Filter} from '../cells/header/cellsHeaderFilters';
import {Palette} from './palette';
import {
  PaletteCategoriesEnum,
  paletteCategoriesRenderer,
  PaletteCustomViewItem,
  PaletteFilterItem,
  PaletteFrameItem,
  PaletteInboxItem,
  PaletteItem,
  PaletteItemTypesEnum,
  PaletteRouteItem,
  PaletteTeammateItem,
} from './paletteCommonTypes';

/*
 * Props.
 */

interface PaletteContainerProps {
  parameters: InboxesParameters;
  onRequestClose: Handler;
}

/*
 * Selectors.
 */

function getParameters(_state: unknown, props: PaletteContainerProps) {
  return props.parameters;
}

type GetCounterVisibility = ReturnType<typeof makeGetCountersVisibility>;
function makeGetMyInboxItems(getCounterVisibility: GetCounterVisibility) {
  return createSelector(
    [getUserOwnedInboxes, getUser, getLocalize, getCounterVisibility, getBoldCounters],
    (privateInboxes, user, localize, counterVisibility, boldCounters) => {
      const inboxesItems = privateInboxes.map<PaletteInboxItem>((inbox) => ({
        type: PaletteItemTypesEnum.INBOX,
        isBold: findBoldCounter(boldCounters, counterVisibility, buildPrivateInboxCounterKey(inbox.id)),
        inbox,
        label: inbox.name,
        itemId: `inbox-${inbox.id}`,
        path: buildTeammateInboxPath(inbox.id, InboxesFiltersEnum.OPEN),
        isPinned: true,
      }));

      const routeItems: ReadonlyArray<ReadonlyArray<PaletteRouteItem>> = [
        [
          {
            type: PaletteItemTypesEnum.ROUTE,
            icon: 'inbox',
            itemId: 'inbox',
            label: localize(t('workspace-inbox')`Inbox`),
            isBold: findBoldCounter(boldCounters, counterVisibility, selfInboxCounterKey),
            path: buildTeammatePath(user.id, TeammatesFoldersEnum.INBOX, InboxesFiltersEnum.OPEN),
            keyboardAction: KeyboardActionsEnum.GO_TO_INBOX,
          },
          {
            type: PaletteItemTypesEnum.ROUTE,
            icon: 'assign',
            itemId: 'assign',
            label: localize(t('workspace-assigned-to-me')`Assigned to me`),
            isBold: findBoldCounter(boldCounters, counterVisibility, selfAssignedCounterKey),
            path: buildTeammatePath(user.id, TeammatesFoldersEnum.ASSIGNED, InboxesFiltersEnum.OPEN),
          },
          {
            type: PaletteItemTypesEnum.ROUTE,
            icon: 'teammates',
            itemId: 'teammates',
            label: localize(t('workspace-sharedwithme')`Shared with me`),
            isBold: findBoldCounter(boldCounters, counterVisibility, sharedWithMeCounterKey),
            path: buildTeammatePath(user.id, TeammatesFoldersEnum.SHARED_WITH_ME, InboxesFiltersEnum.OPEN),
          },
          {
            type: PaletteItemTypesEnum.ROUTE,
            icon: 'chat',
            itemId: 'chat',
            label: localize(t('workspace-discussions')`Discussions`),
            isBold: findBoldCounter(boldCounters, counterVisibility, discussionsCounterKey),
            path: buildTeammatePath(user.id, TeammatesFoldersEnum.DISCUSSIONS, InboxesFiltersEnum.OPEN),
          },
        ],
        [
          {
            type: PaletteItemTypesEnum.ROUTE,
            icon: 'star',
            itemId: 'star',
            label: localize(t('workspace-starred')`Starred`),
            path: `/inboxes/teammates/views/${user.starredTagId}`,
          },
          {
            type: PaletteItemTypesEnum.ROUTE,
            icon: 'draft',
            itemId: 'draft',
            label: localize(t('workspace-drafts')`Drafts`),
            path: buildTeammatePath(user.id, TeammatesMessageFoldersEnum.DRAFTS),
          },
          {
            type: PaletteItemTypesEnum.ROUTE,
            icon: 'send',
            itemId: 'send',
            label: localize(t('workspace-sent')`Sent`),
            path: buildTeammatePath(user.id, TeammatesMessageFoldersEnum.SENT),
          },
        ],
      ];

      const myRouteItems = flatten([...routeItems[0], ...inboxesItems, ...routeItems[1]]).map<
        PaletteInboxItem | PaletteRouteItem
      >((item) => ({
        ...item,
        parentCategoryId: PaletteCategoriesEnum.MY_INBOX,
      }));

      return myRouteItems;
    },
  );
}

const getPersonalWorkspaceVisualCustomViews = makeGetVisualWorkspaceTeammateWorkspaceVisualCustomViews({
  getVisualWorkspaceTags: makeGetUserVisualWorkspaceTeammateWorkspaceTags(getShouldAlphabetizeWorkspace),
  getOptionalAdditionalTagsMap: () => undefined,
  getOptionalParentTagOverridesMap: () => undefined,
  getFindIsItemExpanded: () => dummyFindIsExpanded,
  defaultExpandedState: defaultIsCustomViewExpanded,
  shouldRestrictToVisibleItems: true,
});

function makeGetCustomViews(getCounterVisibility: GetCounterVisibility) {
  return createSelector(
    [getPersonalWorkspaceVisualCustomViews, getCounterVisibility, getBoldCounters, getUserNamespace],
    (visualCustomViews, counterVisibility, boldCounters, userNamespace) =>
      computeFlatVisualCustomViews(visualCustomViews).map<PaletteCustomViewItem>((visualCustomView) => ({
        type: PaletteItemTypesEnum.CUSTOM_VIEW,
        customView: visualCustomView,
        itemId: `customview-${visualCustomView.id}`,
        isBold: visualCustomView.uid
          ? findBoldCounter(
              boldCounters,
              counterVisibility,
              buildCustomViewCounterKey(visualCustomView.uid, visualCustomView.type),
            )
          : false,
        label: visualCustomView.tag.name,
        path: buildCustomViewPath(visualCustomView.id, {
          filter: InboxesFiltersEnum.OPEN,
          isTeammateView: visualCustomView.namespace === userNamespace,
        }),
        parentCategoryId: PaletteCategoriesEnum.TAGS,
      })),
  );
}

function makeGetTeamItems() {
  return createSelector(
    [
      getUserTeams,
      getUserTeamParticipatingInboxes,
      getLocalize,
      getSelectedFilters,
      getShouldUseOpenFilterForTeams,
      getIsInboxVisible,
    ],
    (userTeams, inboxes, localize, selectedFilters, shouldShowTeammateTabsInTeamContext, isInboxVisible) => {
      const inboxesMap: {[k: string]: ReadonlyArray<PaletteItem>} = {};
      const userTeamsMap = _.keyBy(userTeams, (i) => i.namespace);
      const teamContextFilter = shouldShowTeammateTabsInTeamContext
        ? InboxesFiltersEnum.OPEN
        : InboxesFiltersEnum.UNASSIGNED;

      _(inboxes)
        .filter((i) => Boolean(isTeamNamespace(i.namespace) && userTeamsMap[i.namespace]))
        .map<PaletteInboxItem>((inbox) => {
          const teamFilter =
            teamContextFilter ||
            findSelectedFilterForTeam(selectedFilters, userTeamsMap[inbox.namespace]?.id);

          return {
            type: PaletteItemTypesEnum.INBOX,
            isBold: false,
            inbox,
            itemId: `inbox-${inbox.id}`,
            label: inbox.name,
            path: buildTeamInboxPath(inbox.id, teamFilter),
            isPinned: isInboxVisible(inbox.id),
            parentCategoryId: inbox.namespace,
          };
        })
        .forEach((item) => {
          if (!inboxesMap[item.inbox.namespace]) {
            inboxesMap[item.inbox.namespace] = [item];
          } else {
            inboxesMap[item.inbox.namespace] = [...inboxesMap[item.inbox.namespace], item];
          }
        });

      return _(userTeams)
        .map<ReadonlyArray<PaletteItem>>((team) => {
          const teamFilter =
            teamContextFilter || findSelectedFilterForTeam(selectedFilters, userTeamsMap[team.namespace]?.id);

          return [
            {
              type: PaletteItemTypesEnum.TEAM,
              team,
              itemId: `team-${team.id}`,
              path: buildTeamPath(team.id, TeamsFoldersEnum.INBOX, teamFilter),
              label:
                userTeams.length === 1
                  ? paletteCategoriesRenderer[PaletteCategoriesEnum.TEAMS](localize)
                  : team.name,
              isBold: false,
              parentCategoryId: team.namespace,
            },
            ...(inboxesMap[team.namespace] || []),
          ];
        })
        .flatten()
        .compact()
        .value();
    },
  );
}

const getHasSharedOrImplicitPermission = createSelector(
  [getHasPermission, getHasSharedPermission, getUserNamespace],
  (hasPermission, hasSharedPermission, userNamespace) => (permission: PermissionsEnum) =>
    hasSharedPermission(permission) || hasPermission(userNamespace, permission),
);

function makeGetAppTabsItems() {
  return createSelector(
    [getShouldHideAuditTrail, getHasNewContactsManagerV0, getHasSharedOrImplicitPermission, getLocalize],
    (shouldHideAuditTrail, hasNewContactsManagerV0, hasSharedOrImplicitPermission, localize) =>
      buildApps({
        shouldDisplayNewContacts: hasNewContactsManagerV0,
        shouldDisplayAuditTrail: !shouldHideAuditTrail,
        localize,
      })
        .filter((app) => !app.permission || hasSharedOrImplicitPermission(app.permission))
        .map<PaletteItem>((app) => ({
          type: PaletteItemTypesEnum.ROUTE,
          icon: app.iconName,
          path: app.path,
          label: localize(t('palette-openapptab')`${app.localizedTitle}`),
          itemId: `tab-${app.path}`,
          keyboardAction: app.keyboardAction,
          parentCategoryId: PaletteCategoriesEnum.FRONT_APPS,
        })),
  );
}

function makeGetTeammateItems() {
  return createSelector(
    [getWorkspaceTeammatesWithLazyValidationSorted, getSelectedFilters, getFindOnlineStatus],
    (teammates, selectedFilters, findOnlineStatus) =>
      _(teammates)
        .map<PaletteTeammateItem>((teammate) => ({
          type: PaletteItemTypesEnum.TEAMMATE,
          teammate,
          itemId: `teammate-${teammate.id}`,
          onlineStatus: findOnlineStatus(teammate),
          label: buildTeammateDisplayName(teammate),
          path: buildTeammatePath(
            teammate.id,
            TeammatesFoldersEnum.ASSIGNED_SHARED,
            findSelectedFilterForTeammate(selectedFilters, teammate.id),
          ),
          isPinned: true,
          parentCategoryId: PaletteCategoriesEnum.ASSIGNED_CONVERSATIONS,
        }))
        .value(),
  );
}

function makeGetSidePanelFrames() {
  return createSelector([getSidePanelFrames, getLocalize], (sidePanelFrames, localize) =>
    sidePanelFrames
      .map<PaletteFrameItem>((frame) => {
        const frameNames: {[k in FrameTypesEnum]?: string} = {
          [FrameTypesEnum.CALENDAR]: localize(t('palette-calendar')`Calendar`),
          [FrameTypesEnum.CUSTOMER_VIEW]: localize(t('palette-customerview')`Contact details`),
          [FrameTypesEnum.KEYBOARD_HELP]: localize(t('palette-keyboard')`Keyboard shortcuts`),
        };

        const frameKeyboardActions: {[k in FrameTypesEnum]?: KeyboardActionsEnum} = {
          [FrameTypesEnum.KEYBOARD_HELP]: KeyboardActionsEnum.TOGGLE_HELP,
        };

        const frameName = frame.name || frameNames[frame.frameType];

        return {
          type: PaletteItemTypesEnum.FRAME,
          frame,
          itemId: `frame-${frame.frameId}`,
          label: frameName ? localize(t('palette-plugin')`${frameName}`) : '',
          keyboardAction: frameKeyboardActions[frame.frameType],
          parentCategoryId: PaletteCategoriesEnum.INTEGRATIONS,
        };
      })
      .filter((i) => !!i.label),
  );
}

function makeGetNavigationItems(
  getInboxItems: ReturnType<typeof makeGetMyInboxItems>,
  getCustomViewsItems: ReturnType<typeof makeGetCustomViews>,
  getTeamInboxes: ReturnType<typeof makeGetTeamItems>,
  getTeammatesItems: ReturnType<typeof makeGetTeammateItems>,
  getFrameItems: ReturnType<typeof makeGetSidePanelFrames>,
) {
  return createSelector(
    [getInboxItems, getCustomViewsItems, getTeamInboxes, getTeammatesItems, getFrameItems, getLocalize],
    (inboxItems, viewsItems, teamInboxes, teammatesItems, frameItems, localize) =>
      compact<PaletteItem>([
        ...inboxItems,
        ...viewsItems,
        ...teamInboxes,
        ...teammatesItems,
        {
          type: PaletteItemTypesEnum.ACTION,
          icon: 'openPanel',
          itemId: 'toggle-side-panel',
          label: localize(t('palette-sidepanel')`Toggle side panel`),
          keyboardAction: KeyboardActionsEnum.TOGGLE_SIDE_PANEL,
          parentCategoryId: PaletteCategoriesEnum.INTEGRATIONS,
        },
        ...frameItems,
      ]),
  );
}

function makeGetFilters() {
  return createSelector(
    [getParameters, getStarredTagIds, getShouldUseOpenFilterForTeams],
    (parameters, starredTagIds, shouldUseOpenFilterForTeams) =>
      findFilters({
        parameters,
        starredTagIds,
        shouldUseOpenFilterForTeams,
      })?.filters,
  );
}

function makeGetActionItems(getFilters: ReturnType<typeof makeGetFilters>) {
  return createSelector(
    // TODO: If we are adding palette action to the selected conversations
    // we will need to pass in the selection right here
    [getLocalize, getFilters],
    (localize, filters): ReadonlyArray<PaletteItem> => {
      const filtersIcons: {[k in Filter]: IconName} = {
        [InboxesFiltersEnum.UNASSIGNED]: 'unassign',
        [InboxesFiltersEnum.OPEN]: 'inbox',
        [InboxesFiltersEnum.ALL]: 'inbox',
        [InboxesFiltersEnum.ASSIGNED]: 'assignActivity',
        [InboxesFiltersEnum.SNOOZED]: 'snooze',
        [InboxesFiltersEnum.TRASHED]: 'trash',
        [InboxesFiltersEnum.SPAM]: 'spam',
        [InboxesFiltersEnum.ARCHIVED]: 'archive',
        [InboxesSearchLocationsEnum.GLOBAL]: 'searchSmall',
        [InboxesSearchLocationsEnum.LOCAL]: 'searchSmall',
        [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: 'searchSmall',
      };

      const filtersItems: ReadonlyArray<PaletteFilterItem> = !filters
        ? []
        : filters.map((filter) => ({
            type: PaletteItemTypesEnum.FILTER,
            label: localize(t('switch-tofilter')`${renderFilterName(filter, localize)}`),
            itemId: `filter-${filter}`,
            filter,
            icon: filtersIcons[filter],
            parentCategoryId: PaletteCategoriesEnum.FILTERS,
          }));

      return filtersItems;
    },
  );
}

function makeGetTeamsNamesMap() {
  return createSelector([getUserTeams], (teams) =>
    _(teams)
      .keyBy((team) => team.namespace)
      .mapValues((team) => team.name)
      .value(),
  );
}

const mapStateToProps = makeMapStateToProps<PaletteContainerProps>()(() => {
  const getCountersVisibility = makeGetCountersVisibility(getUserNamespace);
  const getTeamInboxes = makeGetTeamItems();
  const getInboxItems = makeGetMyInboxItems(getCountersVisibility);
  const getTeammatesItems = makeGetTeammateItems();
  const getAppTabsItems = makeGetAppTabsItems();
  const getCustomViewItems = makeGetCustomViews(getCountersVisibility);
  const getNavigationItems = makeGetNavigationItems(
    getInboxItems,
    getCustomViewItems,
    getTeamInboxes,
    getTeammatesItems,
    makeGetSidePanelFrames(),
  );
  const getFilters = makeGetFilters();

  return {
    items: getNavigationItems,
    actionItems: makeGetActionItems(getFilters),
    appTabsItems: getAppTabsItems,
    parameters: getParameters,
    selectedFrameId: getSelectedSideFrameId,
    lastSelectedFrameId: getLastSelectedSideFrameId,
    teamsNamesMap: makeGetTeamsNamesMap(),
  };
});

export const PaletteContainer = connect(
  mapStateToProps,
  {
    navigateToLinkAction,
    updateSelectedSideFrameIdAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {parameters} = stateProps;

    const openLink = (path: string) => dispatchProps.navigateToLinkAction(path);
    const switchToFilter = (filter: Filter) => {
      if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
        return;
      }

      if (!RInboxesFilter.is(filter)) {
        return;
      }

      const newParameters: InboxesConversationsParameters = {
        ...parameters,
        selection: emptyInboxesSelection,
        activity: undefined,
        filters: [filter],
      };
      dispatchProps.navigateToLinkAction(buildParametersPath(newParameters));
    };

    const openSideFrame = (frameId: string | undefined) => {
      dispatchProps.updateSelectedSideFrameIdAction(frameId);
    };

    const onKeyboardAction = (keyboardAction: KeyboardActionsEnum) => {
      if (keyboardAction === KeyboardActionsEnum.TOGGLE_SIDE_PANEL) {
        if (stateProps.selectedFrameId) {
          dispatchProps.updateSelectedSideFrameIdAction(undefined);
        } else {
          dispatchProps.updateSelectedSideFrameIdAction(stateProps.lastSelectedFrameId);
        }
      }
    };

    return {
      ...ownProps,
      ...stateProps,
      openLink,
      switchToFilter,
      openSideFrame,
      onKeyboardAction,
    };
  },
)(Palette);

/*
 * Helpers.
 */

type FilterNames = {[T in Filter]?: LocalizeRenderer};
const filterNames: FilterNames = {
  [InboxesFiltersEnum.UNASSIGNED]: (localize) => localize(t('cell-filter-unassigned')`Unassigned`),
  [InboxesFiltersEnum.OPEN]: (localize) => localize(t('cell-filter-open')`Open`),
  [InboxesFiltersEnum.ALL]: (localize) => localize(t('cell-filter-all')`All`),
  [InboxesFiltersEnum.ASSIGNED]: (localize) => localize(t('cell-filter-assigned')`Assigned`),
  [InboxesFiltersEnum.SNOOZED]: (localize) => localize(t('cell-filter-snoozed')`Snoozed`),
  [InboxesFiltersEnum.TRASHED]: (localize) => localize(t('cell-filter-trash')`Trash`),
  [InboxesFiltersEnum.SPAM]: (localize) => localize(t('cell-filter-spam')`Spam`),
  [InboxesFiltersEnum.ARCHIVED]: (localize) => localize(t('cell-filter-archived')`Archived`),
  [InboxesSearchLocationsEnum.GLOBAL]: (localize) => localize(t('cell-filter-allinboxes')`All inboxes`),
  [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: (localize) =>
    localize(t('cell-filter-sidebar')`My sidebar`),
};

function renderFilterName<T extends Filter>(filter: T, localize: Localizer) {
  const maybeFilterRenderer = filterNames[filter];

  // Otherwise, use the localization map.
  return maybeFilterRenderer ? maybeFilterRenderer(localize) : null;
}
